import { langGraphClient, getLangGraphClient, LangGraphClient } from '@/lib/langgraph-client-v2';
import type {
  StreamCallbacks,
  StreamController,
  ThreadMetadata,
  LangGraphMessage,
  LangGraphMessageInput,
} from '@/types/langgraph';
import type { CurrentTeam, TeamLeader } from '@/types/onboarding.types';
import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';

/**
 * Input for sendMessageStream - supports both string (backward compatible) and message array
 */
export type SendMessageInput = string | LangGraphMessageInput[];

/**
 * Thread history data containing both messages and UI events
 */
export interface ThreadHistoryData {
  messages: LangGraphMessage[];
  uiEvents: any[];
  currentTeam: CurrentTeam | null;
  teamLeader: TeamLeader | null;
}

/**
 * Chat Assistant Service
 *
 * Service layer for chat operations using LangGraph Client
 * Uses error tuple pattern: [Error | null, Data | null]
 */
export const chatAssistantService = {
  /**
   * Create a new thread in LangGraph
   * Returns error tuple pattern
   */
  createThread: async (
    userId: string,
    metadata?: Record<string, any>,
    client?: LangGraphClient
  ): Promise<[Error | null, ThreadMetadata | null]> => {
    try {
      const c = client ?? langGraphClient;
      const thread = await c.createThread(metadata);

      const threadMetadata: ThreadMetadata = {
        id: thread.thread_id,
        langGraphThreadId: thread.thread_id,
        userId,
        title: metadata?.title || 'New conversation',
        lastMessageAt: Date.now(),
        messageCount: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      return [null, threadMetadata];
    } catch (error) {
      console.error('Failed to create thread:', error);
      return [error as Error, null];
    }
  },

  /**
   * Get thread history from LangGraph
   * Returns error tuple pattern with both messages and UI events
   */
  getThreadHistory: async (
    threadId: string
  ): Promise<[Error | null, ThreadHistoryData | null]> => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
      const [state, currentTeamResponse] = await Promise.all([
        langGraphClient.getThreadState(threadId),
        fetch(`${appConfig.API_BASE_URL}/threads/${threadId}/current-team`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);

      // Extract team leader from onboarding_metadata (LangGraph state)
      const metadata = state.values.onboarding_metadata;
      const teamLeader: TeamLeader | null = metadata?.team_leader || null;

      // Use /current-team API as source of truth (reads from DB thread_metadata)
      // Fall back to LangGraph state if API returns null
      const currentTeam: CurrentTeam | null =
        currentTeamResponse || metadata?.current_team || null;

      return [
        null,
        {
          messages: state.values.messages || [],
          uiEvents: state.values.ui || [],
          currentTeam,
          teamLeader,
        },
      ];
    } catch (error) {
      console.error('Failed to get thread history:', error);
      return [error as Error, null];
    }
  },

  /**
   * Send message with streaming response
   * Supports both string input (backward compatible) and message array for tool messages
   * Returns error tuple pattern
   */
  sendMessageStream: async (
    threadId: string,
    input: SendMessageInput,
    callbacks: StreamCallbacks,
    signal?: AbortSignal,
    client?: LangGraphClient
  ): Promise<[Error | null, StreamController | null]> => {
    try {
      const c = client ?? langGraphClient;

      // Convert input to message array format
      let messages: LangGraphMessageInput[];

      if (typeof input === 'string') {
        // Backward compatibility: string input becomes human message
        messages = [
          {
            type: 'human' as const,
            content: input,
          },
        ];
      } else {
        // New format: use message array directly
        messages = input;
      }

      const runInput = {
        input: {
          messages: messages,
        },
        streamMode: 'messages' as const,
      };

      const controller = await c.streamRun(
        threadId,
        runInput,
        callbacks,
        signal
      );

      return [null, controller];
    } catch (error) {
      console.error('Failed to send message stream:', error);
      return [error as Error, null];
    }
  },

  /**
   * Handle interrupt and resume run
   * Returns error tuple pattern
   */
  handleInterrupt: async (
    threadId: string,
    runId: string,
    response: any
  ): Promise<[Error | null, boolean]> => {
    try {
      await langGraphClient.resumeRun(threadId, runId, response);
      return [null, true];
    } catch (error) {
      console.error('Failed to handle interrupt:', error);
      return [error as Error, false];
    }
  },

  /**
   * Get current team for a thread
   * Returns error tuple pattern
   */
  getCurrentTeam: async (
    threadId: string
  ): Promise<[Error | null, CurrentTeam | null]> => {
    try {
      const url = `${appConfig.API_BASE_URL}/threads/${threadId}/current-team`;
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return [null, data];
    } catch (error) {
      console.error('Failed to get current team:', error);
      return [error as Error, null];
    }
  },

};
